/*
 * Renders a group of radio buttons that can be used to select
 * a single value from a set of options.
 *
 * The corresponding component when rendering the selected
 * value is PropertyGroup.
 *
 */

import React from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';
import { ValidationError } from '../../components';

import css from './FieldRadioButtonGroup.module.css';

// IconRadioButton component (copied from FieldRadioButton)
const IconRadioButton = props => {
  const { className, checkedClassName, showAsRequired } = props;
  return (
    <div>
      <svg className={className} width="14" height="14" xmlns="http://www.w3.org/2000/svg">
        <circle
          className={showAsRequired ? css.required : css.notChecked}
          cx="5"
          cy="19"
          r="6"
          transform="translate(2 -12)"
          strokeWidth="2"
          fill="none"
          fillRule="evenodd"
        />

        <g
          className={classNames(css.checked, checkedClassName || css.checkedStyle)}
          transform="translate(2 -12)"
          fill="none"
          fillRule="evenodd"
        >
          <circle strokeWidth="2" cx="5" cy="19" r="6" />
          <circle fill="#FFF" fillRule="nonzero" cx="5" cy="19" r="3" />
        </g>
      </svg>
    </div>
  );
};

const FieldRadioButtonRenderer = props => {
  const {
    className,
    rootClassName,
    selectClassName,
    label,
    optionLabelClassName,
    orientation,
    layout = 'default', // New prop for layout type
    onChange,
    showLabelAsDisabled,
    id,
    name,
    options,
    meta,
    input,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = touched && invalid && error;
  const isButtonGroup = layout === 'buttonGroup';

  const listClasses = classNames({
    [css.list]: !isButtonGroup,
    [css.buttonGroup]: isButtonGroup,
    [css.horizontal]: orientation === 'horizontal',
    [selectClassName]: selectClassName,
    [css.selectError]: hasError,
  });

  const handleChange = e => {
    input.onChange(e);
    if (onChange) {
      onChange(e.currentTarget.value);
    }
  };

  const Tag = label ? 'fieldset' : 'div';

  const classes = classNames(rootClassName || css.root, className);
  return (
    <Tag className={classes}>
      {label ? (
        <label htmlFor={id} className={classNames({ [css.labelDisabled]: showLabelAsDisabled })}>
          {label}
        </label>
      ) : null}
      <ul className={listClasses}>
        {options.map((option, index) => {
          const fieldId = `${id}.${option.key}`;
          const textClassName = optionLabelClassName;
          const textClassNameMaybe = textClassName ? { textClassName } : {};
          const isChecked = input.value === option.key;

          // Different class names based on layout
          const itemClassName = isButtonGroup ? css.buttonGroupItem : css.item;
          const inputClassName = isButtonGroup ? css.buttonGroupInput : css.input;
          const labelClassName = isButtonGroup ? css.buttonGroupLabel : css.label;
          const textClass = isButtonGroup ? css.buttonGroupText : css.text;
          const dataProps = option.slug ? { 'data-slug': option.slug } : {};

          return (
            <li key={fieldId} className={itemClassName}>
              <span className={isButtonGroup ? css.radioButtonRoot : ''}>
                <input
                  id={fieldId}
                  name={input.name}
                  type="radio"
                  value={option.key}
                  checked={isChecked}
                  onChange={handleChange}
                  className={inputClassName}
                  {...dataProps}
                  {...rest}
                />
                <label htmlFor={fieldId} className={labelClassName}>
                  {!isButtonGroup && (
                    <span className={css.radioButtonWrapper}>
                      <IconRadioButton
                        className={textClassName}
                        checkedClassName={css.checkedStyle}
                      />
                    </span>
                  )}
                  <span className={textClass}>{option.label}</span>
                </label>
              </span>
            </li>
          );
        })}
      </ul>
      <ValidationError fieldMeta={{ ...meta }} />
    </Tag>
  );
};

// Note: name and component are required fields for Field.
// Component-prop we define in this file, name needs to be passed in

/**
 * @typedef {Object} RadioButtonGroupOption
 * @property {string} key
 * @property {string} label
 */

/**
 * Final Form Field containing radio button group.
 * Renders a group of radio buttons that can be used to select
 * a single value from a set of options.
 *
 * The corresponding component when rendering the selected
 * value is PropertyGroup.
 *
 * @component
 * @param {Object} props
 * @param {string} props.name this is required for Field (Final Form component)
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.selectClassName add more style rules to <select> component
 * @param {string?} props.optionLabelClassName given to each option
 * @param {string} props.id given to input
 * @param {ReactNode} props.label the label for the radio button group
 * @param {Array<RadioButtonGroupOption>} props.options E.g. [{ key, label }]
 * @param {string} props.orientation the orientation of the radio buttons, either 'horizontal' or 'vertical'
 * @param {string} props.layout the layout style, either 'default' or 'buttonGroup'
 * @param {boolean} props.disabled Whether the radio group element is disabled
 * @param {boolean} props.showLabelAsDisabled Whether the label is disabled
 * @returns {JSX.Element} Final Form Field containing multiple radio button inputs
 */
const FieldRadioButtonGroup = props => <Field component={FieldRadioButtonRenderer} type="select" {...props} />;

export default FieldRadioButtonGroup;
